import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment,
    Link,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'

import { Container, FloorplanThumb } from '../../components'
import { axiosInstance } from '../../helpers'
import {
    BidStatus,
    RFPStatus,
    GeneratedRFPArea,
    GeneratedRFPInsurance,
    GeneratedRFPMixGroup,
    GeneratedRFPMixUnitType,
    GeneratedRFPUnitMix,
} from '../../models'
import { getServiceImageDetails } from '../../models/WorkOrder/services'
import { typographyStyle } from '../../theme/typographyStyle'

interface BidRFPPayload {
    apartment_name: string
    vendor_name: string
    submitted: boolean
    declined: boolean
    rfp: {
        id: number
        name: string
        status?: RFPStatus
        description: string
        project_start_date: string | null
        project_end_date: string | null
        project_submission_deadline: string | null
        service: { id: number; name: string } | null
        unit_mix: GeneratedRFPUnitMix[]
        mix_groups: GeneratedRFPMixGroup[]
        areas: GeneratedRFPArea[]
        move_out_histogram: { date: string; count: number }[]
        move_out_date: string | null
        move_in_date: string | null
        insurance: GeneratedRFPInsurance | null
        notes?: string
        documents: { id: number; file: string | null; file_name: string }[]
    }
    bid: {
        id: number
        status: BidStatus
        notes: string
        bid_items: {
            unit_config_id: number | null
            area_config_id: number | null
            price: string | null
        }[]
        has_vendor_coi: boolean
    }
}

const isValidDecimal = (value: string) =>
    value === '' || /^\d*(\.\d{0,2})?$/.test(value)

const formatShortDate = (value?: string | null) => {
    if (!value) {
        return '—'
    }
    const [year, month, day] = value.slice(0, 10).split('-').map(Number)
    if (!year || !month || !day) {
        return value
    }
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const formatCount = (value: number | null | undefined) =>
    value == null ? 'TBD' : String(value)

const formatSqft = (row: {
    min_square_footage?: number | null
    max_square_footage?: number | null
}) => {
    if (row.min_square_footage && row.max_square_footage) {
        if (row.min_square_footage === row.max_square_footage) {
            return `${row.min_square_footage.toLocaleString()} sq ft`
        }
        return `${row.min_square_footage.toLocaleString()}–${row.max_square_footage.toLocaleString()} sq ft`
    }
    if (row.min_square_footage) {
        return `${row.min_square_footage.toLocaleString()} sq ft`
    }
    if (row.max_square_footage) {
        return `${row.max_square_footage.toLocaleString()} sq ft`
    }
    return null
}

const mixGroupsFromPayload = (data: BidRFPPayload): GeneratedRFPMixGroup[] => {
    if (data.rfp.mix_groups?.length) {
        return data.rfp.mix_groups
    }
    if (!data.rfp.unit_mix?.length) {
        return []
    }
    return [
        {
            bed_count: null,
            bath_count: null,
            label: 'Unit types',
            full_count: null,
            partial_count: null,
            source: 'tbd',
            unit_types: data.rfp.unit_mix.map((unit) => ({
                unit_config_id: unit.unit_config_id,
                name: unit.name,
                bed_count: unit.bed_count,
                bath_count: unit.bath_count,
                min_square_footage: unit.min_square_footage,
                max_square_footage: unit.max_square_footage,
                floorplan_image: unit.floorplan_image,
            })),
        },
    ]
}

const pricesFromBid = (data: BidRFPPayload) => {
    const unitPrices: Record<number, string> = {}
    const areaPrices: Record<number, string> = {}
    data.bid.bid_items.forEach((item) => {
        if (item.unit_config_id && item.price) {
            unitPrices[item.unit_config_id] = item.price
        }
        if (item.area_config_id && item.price) {
            areaPrices[item.area_config_id] = item.price
        }
    })
    return { unitPrices, areaPrices }
}

export const BidRFPPage = () => {
    const theme = useTheme()
    const { link_token } = useParams<{ link_token: string }>()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [payload, setPayload] = useState<BidRFPPayload | null>(null)
    const [unitPrices, setUnitPrices] = useState<Record<number, string>>({})
    const [areaPrices, setAreaPrices] = useState<Record<number, string>>({})
    const [notes, setNotes] = useState('')
    const [coiFile, setCoiFile] = useState<File | null>(null)
    const [saving, setSaving] = useState(false)
    const [justSubmitted, setJustSubmitted] = useState(false)

    const applyPayload = (data: BidRFPPayload) => {
        setPayload(data)
        const prices = pricesFromBid(data)
        setUnitPrices(prices.unitPrices)
        setAreaPrices(prices.areaPrices)
        setNotes(data.bid.notes || '')
        setError(null)
    }

    useEffect(() => {
        setJustSubmitted(false)
        setLoading(true)
        axiosInstance
            .get<BidRFPPayload>('service-contract/rfp-bid-link/', {
                params: { link_token },
            })
            .then((res) => applyPayload(res.data))
            .catch((err) => {
                setError(
                    err?.response?.data?.error ||
                        'This bid link is invalid or expired.',
                )
            })
            .finally(() => setLoading(false))
    }, [link_token])

    const deadlinePassed = Boolean(
        payload?.rfp.project_submission_deadline &&
            !Number.isNaN(
                new Date(payload.rfp.project_submission_deadline).getTime(),
            ) &&
            Date.now() >
                new Date(payload.rfp.project_submission_deadline).getTime(),
    )
    const rfpClosed = Boolean(
        payload?.rfp.status &&
            payload.rfp.status !== RFPStatus.PUBLISHED &&
            payload.rfp.status !== RFPStatus.SELECT_VENDOR,
    )
    const locked = Boolean(
        payload &&
            (payload.declined ||
                payload.bid.status === BidStatus.ACCEPTED ||
                deadlinePassed ||
                rfpClosed ||
                justSubmitted),
    )
    const canDecline = Boolean(
        payload &&
            !payload.declined &&
            !justSubmitted &&
            !deadlinePassed &&
            !rfpClosed &&
            payload.bid.status !== BidStatus.SUBMITTED &&
            payload.bid.status !== BidStatus.REVIEWED &&
            payload.bid.status !== BidStatus.ACCEPTED,
    )

    const mixGroups = useMemo(
        () => (payload ? mixGroupsFromPayload(payload) : []),
        [payload],
    )
    const areas = payload?.rfp.areas || []
    const unitTypes = useMemo(
        () => mixGroups.flatMap((group) => group.unit_types),
        [mixGroups],
    )

    const submit = async (action: 'submit' | 'decline') => {
        if (!link_token) {
            return
        }
        if (action === 'submit') {
            const missingArea = areas.some(
                (area) => !areaPrices[area.area_config_id],
            )
            const missingUnit = unitTypes.some(
                (unit) => !unitPrices[unit.unit_config_id],
            )
            const invalid = [
                ...Object.values(areaPrices),
                ...Object.values(unitPrices),
            ].some((value) => value !== '' && !isValidDecimal(value))
            if (missingArea || missingUnit) {
                toast.error('Enter a price for every area and unit type.')
                return
            }
            if (invalid) {
                toast.error('Enter valid prices (e.g. 12.34).')
                return
            }
        }
        setSaving(true)
        const body = new FormData()
        body.append('link_token', link_token)
        body.append('action', action)
        body.append('notes', notes)
        body.append(
            'bid_items',
            JSON.stringify([
                ...areas.map((area) => ({
                    area_config_id: area.area_config_id,
                    price: areaPrices[area.area_config_id],
                })),
                ...unitTypes.map((unit) => ({
                    unit_config_id: unit.unit_config_id,
                    price: unitPrices[unit.unit_config_id],
                })),
            ]),
        )
        if (coiFile) {
            body.append('file', coiFile)
            body.append('name', coiFile.name)
        }
        try {
            const res = await axiosInstance.post<BidRFPPayload>(
                'service-contract/rfp-bid-link/',
                body,
            )
            applyPayload(res.data)
            setJustSubmitted(action === 'submit')
            toast.success(
                action === 'decline'
                    ? 'You declined this request.'
                    : 'Your pricing was submitted.',
            )
        } catch (err) {
            const axiosErr = err as AxiosError<{ error?: string }>
            toast.error(
                axiosErr.response?.data?.error || 'Unable to save this bid.',
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !payload) {
        return (
            <Box padding={4} maxWidth={640} margin="0 auto">
                <Typography>{error}</Typography>
            </Box>
        )
    }

    const priceField = (value: string, onChange: (next: string) => void) => (
        <TextField
            variant="outlined"
            size="small"
            label="Price"
            placeholder="0.00"
            type="number"
            value={value}
            disabled={locked}
            onChange={(event) => {
                const next = event.target.value
                if (isValidDecimal(next)) {
                    onChange(next)
                }
            }}
            error={value !== '' && !isValidDecimal(value)}
            helperText={
                value !== '' && !isValidDecimal(value)
                    ? 'Enter a valid amount'
                    : undefined
            }
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                ),
            }}
            inputProps={{
                step: '0.01',
                min: '0',
                style: { textAlign: 'right' },
            }}
            style={{ width: 132, marginTop: 8 }}
        />
    )

    const renderUnitType = (unit: GeneratedRFPMixUnitType) => {
        const sqft = formatSqft(unit)
        const thumbStyle: React.CSSProperties = {
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 4,
            marginBottom: 8,
        }
        return (
            <Box key={unit.unit_config_id} style={{ minWidth: 0 }}>
                <FloorplanThumb
                    src={unit.floorplan_image}
                    alt={`${unit.name} floor plan`}
                    thumbStyle={thumbStyle}
                />
                <Typography style={{ fontWeight: 600 }}>{unit.name}</Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', minHeight: 18 }}
                >
                    {sqft || '\u00a0'}
                </Typography>
                {priceField(unitPrices[unit.unit_config_id] || '', (next) =>
                    setUnitPrices((prev) => ({
                        ...prev,
                        [unit.unit_config_id]: next,
                    })),
                )}
            </Box>
        )
    }

    return (
        <Box padding={4} maxWidth={960} margin="0 auto">
            {(() => {
                const serviceDetails = getServiceImageDetails(
                    payload.rfp.service?.id ?? -1,
                )
                const accent =
                    serviceDetails.color || theme.palette.primary.main
                const tileStyle: React.CSSProperties = {
                    background: '#F7F8F8',
                    border: '1px solid #E6E8E8',
                    borderRadius: 8,
                    padding: '10px 14px',
                    minWidth: 0,
                }
                const tileLabelStyle: React.CSSProperties = {
                    color: theme.palette.grey[600],
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    fontSize: 11,
                }
                const tileValueStyle: React.CSSProperties = {
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: 1.35,
                    marginTop: 2,
                }
                const extraDocuments = payload.rfp.documents.filter((doc) => {
                    const insurance = payload.rfp.insurance
                    if (!doc.file) {
                        return false
                    }
                    if (!insurance) {
                        return true
                    }
                    return (
                        doc.file !== insurance.file &&
                        doc.file_name !== insurance.name
                    )
                })
                return (
                    <Box
                        style={{
                            border: '1px solid #E6E8E8',
                            borderRadius: 12,
                            overflow: 'hidden',
                            marginBottom: 28,
                            background: theme.palette.common.white,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="stretch"
                            style={{ minHeight: 6 }}
                        >
                            <Box
                                style={{ width: 6, backgroundColor: accent }}
                            />
                            <Box flex={1} style={{ padding: '18px 20px 16px' }}>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    style={{ gap: 12 }}
                                >
                                    <Box style={{ minWidth: 0 }}>
                                        <Typography
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.h4,
                                                ),
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {payload.apartment_name}
                                        </Typography>
                                        <Typography
                                            color="textSecondary"
                                            style={{ marginTop: 4 }}
                                        >
                                            Pricing sheet for{' '}
                                            {payload.vendor_name}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        icon={
                                            serviceDetails.icon ? (
                                                <img
                                                    src={serviceDetails.icon}
                                                    alt=""
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                    }}
                                                />
                                            ) : undefined
                                        }
                                        label={
                                            payload.rfp.service?.name ||
                                            'Service'
                                        }
                                        style={{
                                            fontWeight: 700,
                                            backgroundColor: `${accent}14`,
                                            color: accent,
                                            height: 28,
                                        }}
                                    />
                                </Box>
                                <Box
                                    display="flex"
                                    style={{ gap: 10, marginTop: 16 }}
                                    alignItems="stretch"
                                >
                                    <Box flex={1.4} style={tileStyle}>
                                        <Typography style={tileLabelStyle}>
                                            Window
                                        </Typography>
                                        <Typography style={tileValueStyle}>
                                            {formatShortDate(
                                                payload.rfp.project_start_date,
                                            )}{' '}
                                            –{' '}
                                            {formatShortDate(
                                                payload.rfp.project_end_date,
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box flex={0.8} style={tileStyle}>
                                        <Typography style={tileLabelStyle}>
                                            Bid by
                                        </Typography>
                                        <Typography style={tileValueStyle}>
                                            {formatShortDate(
                                                payload.rfp
                                                    .project_submission_deadline,
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box flex={1} style={tileStyle}>
                                        <Typography style={tileLabelStyle}>
                                            Insurance
                                        </Typography>
                                        {!payload.rfp.insurance?.file ? (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                style={{
                                                    display: 'block',
                                                    marginTop: 2,
                                                }}
                                            >
                                                {coiFile
                                                    ? coiFile.name
                                                    : payload.bid.has_vendor_coi
                                                    ? 'Your COI is on file'
                                                    : 'None attached'}
                                            </Typography>
                                        ) : null}
                                        {payload.rfp.insurance
                                            ?.gl_each_occurrence ? (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                style={{ display: 'block' }}
                                            >
                                                GL $
                                                {
                                                    payload.rfp.insurance
                                                        .gl_each_occurrence
                                                }
                                            </Typography>
                                        ) : null}
                                        {payload.rfp.insurance?.file ? (
                                            <Button
                                                href={
                                                    payload.rfp.insurance.file
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                variant="outlined"
                                                size="small"
                                                style={{
                                                    textTransform: 'none',
                                                    marginTop: 8,
                                                }}
                                            >
                                                View sample COI
                                            </Button>
                                        ) : null}
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            size="small"
                                            disabled={locked}
                                            style={{
                                                textTransform: 'none',
                                                marginTop: 8,
                                            }}
                                        >
                                            {coiFile
                                                ? 'Replace COI'
                                                : payload.rfp.insurance?.file ||
                                                  payload.bid.has_vendor_coi
                                                ? 'Attach your COI'
                                                : 'Attach COI'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="application/pdf"
                                                onChange={(event) => {
                                                    setCoiFile(
                                                        event.target
                                                            .files?.[0] || null,
                                                    )
                                                    event.target.value = ''
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                </Box>
                                <Box
                                    style={{
                                        ...tileStyle,
                                        marginTop: 10,
                                    }}
                                >
                                    <Typography style={tileLabelStyle}>
                                        Notes
                                    </Typography>
                                    {payload.rfp.notes ? (
                                        <Typography
                                            style={{
                                                fontSize: 14,
                                                lineHeight: 1.5,
                                                marginTop: 4,
                                                marginBottom: 8,
                                            }}
                                        >
                                            {payload.rfp.notes}
                                        </Typography>
                                    ) : null}
                                    <TextField
                                        placeholder="Add a note for the property (optional)"
                                        value={notes}
                                        onChange={(event) =>
                                            setNotes(event.target.value)
                                        }
                                        disabled={locked}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        size="small"
                                        style={{
                                            marginTop: payload.rfp.notes
                                                ? 0
                                                : 8,
                                        }}
                                    />
                                </Box>
                                {extraDocuments.length > 0 ? (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 12, marginTop: 10 }}
                                    >
                                        {extraDocuments.map((doc) => (
                                            <Link
                                                key={doc.id}
                                                href={doc.file || undefined}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {doc.file_name}
                                            </Link>
                                        ))}
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                )
            })()}

            <Box marginTop={4} marginBottom={5}>
                <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                    Area (Partial Pricing)
                </Typography>
                <Divider />
                {areas.length === 0 ? (
                    <Typography color="textSecondary" style={{ marginTop: 12 }}>
                        No area pricing for this property.
                    </Typography>
                ) : (
                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                            gap: 16,
                            marginTop: 16,
                        }}
                    >
                        {areas.map((area) => (
                            <Box
                                key={area.area_config_id}
                                style={{ minWidth: 0 }}
                            >
                                <Typography>{area.name}</Typography>
                                {priceField(
                                    areaPrices[area.area_config_id] || '',
                                    (next) =>
                                        setAreaPrices((prev) => ({
                                            ...prev,
                                            [area.area_config_id]: next,
                                        })),
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <Box marginBottom={4}>
                <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                    Unit Types (Whole Unit Pricing)
                </Typography>
                <Divider />
                {mixGroups.length === 0 ? (
                    <Typography color="textSecondary" style={{ marginTop: 12 }}>
                        No unit types for this property.
                    </Typography>
                ) : (
                    mixGroups.map((group) => (
                        <Box key={group.label} style={{ marginTop: 20 }}>
                            <Typography style={{ fontWeight: 700 }}>
                                {group.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 12 }}
                            >
                                We expect ~{formatCount(group.full_count)} full
                                · {formatCount(group.partial_count)} partial
                                {group.source === 'tbd'
                                    ? ' (counts TBD)'
                                    : ' (estimates only)'}
                            </Typography>
                            <Box
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(3, minmax(0, 1fr))',
                                    gap: 16,
                                }}
                            >
                                {group.unit_types.map(renderUnitType)}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            {locked ? (
                <Typography style={{ marginTop: 24 }}>
                    {payload.declined
                        ? 'You declined this request.'
                        : deadlinePassed
                        ? 'The submission deadline has passed.'
                        : rfpClosed || payload.bid.status === BidStatus.ACCEPTED
                        ? 'This request is no longer accepting updates.'
                        : 'This pricing has been submitted.'}
                </Typography>
            ) : (
                <Container style={{ gap: 12, marginTop: 24 }}>
                    {canDecline ? (
                        <Button
                            variant="outlined"
                            disabled={saving}
                            onClick={() => submit('decline')}
                            style={{ textTransform: 'none' }}
                        >
                            Decline
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        onClick={() => submit('submit')}
                        style={{ textTransform: 'none' }}
                    >
                        {saving ? 'Saving…' : 'Submit pricing'}
                    </Button>
                </Container>
            )}
        </Box>
    )
}
